/**
 * Framing price engine.
 *
 * Lifted out of `components/internal/FramingCalculator.vue` so the exact same numbers can be
 * produced without the UI: an artwork set takes ONE spec selection (made on the first piece) and
 * prices it against every other piece's own display size. The component still owns the spec state
 * and the presentation — it just no longer owns the arithmetic.
 *
 * The formulas mirror the authoritative pricing sheet rung for rung (including the ladder's first
 * strict comparison). Do not "simplify" them.
 */

export type FramingArtworkType = 'Paper' | 'Canvas';

/**
 * What the buyer is having done to the work.
 *
 * 'frame' is the full build. 'stretch' is offered on canvas only: the work is mounted on stretcher
 * bars and hung as-is, so the stretcher is the only component that applies — no frame, and none of
 * the paper-only components either.
 */
export type FramingMode = 'frame' | 'stretch';

/** The spec selections that drive pricing. `frameColor` is visual-only and deliberately absent. */
export interface FramingSpecs {
  wantMount: boolean;
  mountType: string;
  mountWidth: number;
  glassType: string;
  boardType: string;
  wantSideDepth: boolean;
  sideDepthRate: number;
  sheetRate: number;
  stretcherBar: string;
  frameType: string;
  frameMaterial: string;
  frameFront: number;
  frameDepth: number;
  floaterSpace: number;
  frameFinish: string;
}

export interface FramingComponentResult {
  applicable: boolean;
  notApplicable?: boolean;
  outOfRange?: boolean;
  Lin?: number;
  Hin?: number;
  Lft?: number;
  Hft?: number;
  area?: number;
  perimeter?: number;
  totalPerimeter?: number;
  shape?: string;
  rate?: number;
  baseRate?: number;
  finishAddOn?: number;
  finalRate?: number;
  cost?: number;
}

export interface FramingComponents {
  mount: FramingComponentResult;
  glass: FramingComponentResult;
  board: FramingComponentResult;
  sideDepth: FramingComponentResult;
  sheet: FramingComponentResult;
  stretcher: FramingComponentResult;
  frame: FramingComponentResult;
}

export interface FramingBreakdown {
  mode: FramingMode;
  components: FramingComponents;
  total: number;
  /** True when a size falls outside the pricing sheet's ladder, so the total is incomplete. */
  outOfRange: boolean;
  /** True when the artwork's dimensions could not be read at all — nothing was priced. */
  invalidSize: boolean;
}

const LADDER_IN = [12, 17, 23, 29, 36, 42, 48, 54, 60, 66, 72, 78, 84, 90, 96, 102, 108, 114, 120];
const LADDER_FT = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];

export function ladderFeet(inches: number): number | null {
  // The sheet's first rung is the only one that compares strictly: IF(x<12,1,IF(x<=17,1.5,...)),
  // so exactly 12" bills as 1.5 ft, not 1 ft. Every later rung is inclusive.
  if (inches < LADDER_IN[0]!) return LADDER_FT[0]!;
  for (let i = 1; i < LADDER_IN.length; i++) {
    if (inches <= LADDER_IN[i]!) return LADDER_FT[i]!;
  }
  return null;
}

const GLASS_RATES: Record<string, number> = {
  "Clear Modiguard-2mm": 80, "Clear Modiguard-3.5mm": 100,
  "Acrylic Sheet-2mm": 200, "Acrylic Sheet-3.5mm": 300
};
const BOARD_RATES: Record<string, number> = { "Western Backboard": 60, "White Sunboard": 20 };
const STRETCHER_RATES: Record<string, number> = { "1x1": 60, "1x2": 100, "1.5x2": 150, "2x2": 200 };
const FINISH_ADDON: Record<string, number> = { "Natural Lacquer": 0, "Painted Polished": 100, "Double shade": 150 };

/** Airtable sends the category as an option name; everything that is not Paper prices as Canvas. */
export function resolveFramingArtworkType(artworkCategory?: string | null): FramingArtworkType {
  return (artworkCategory ?? '').trim() === 'Paper' ? 'Paper' : 'Canvas';
}

/** The calculator's own starting selection, and the fallback for anything missing from a spec. */
export const DEFAULT_FRAMING_SPECS: FramingSpecs = {
  wantMount: false,
  mountType: 'Single Mount',
  mountWidth: 1,
  glassType: 'Clear Modiguard-2mm',
  boardType: 'Western Backboard',
  wantSideDepth: false,
  sideDepthRate: 100,
  sheetRate: 20,
  stretcherBar: '1.5x2',
  frameType: 'Classic Frame',
  frameMaterial: 'Wooden',
  frameFront: 1,
  frameDepth: 2,
  floaterSpace: 1,
  frameFinish: 'Painted Polished',
};

const num = (value: unknown, fallback: number) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
const pick = <T>(value: unknown, table: Record<string, T>, fallback: string) =>
  (typeof value === 'string' && value in table) ? value : fallback;

/**
 * Fill in anything a spec object is missing.
 *
 * The calculator emits `floaterSpace`/`frameFinish` as undefined when the current selection makes
 * them irrelevant, and those same specs may later be re-priced against a piece where they ARE
 * relevant — without this, one missing field turns a whole rate into NaN.
 */
function sanitizeSpecs(specs: Partial<FramingSpecs> | null | undefined): FramingSpecs {
  const s = specs ?? {};
  return {
    wantMount: Boolean(s.wantMount),
    mountType: s.mountType === 'Double Mount' ? 'Double Mount' : 'Single Mount',
    mountWidth: num(s.mountWidth, DEFAULT_FRAMING_SPECS.mountWidth),
    glassType: pick(s.glassType, GLASS_RATES, DEFAULT_FRAMING_SPECS.glassType),
    boardType: pick(s.boardType, BOARD_RATES, DEFAULT_FRAMING_SPECS.boardType),
    wantSideDepth: Boolean(s.wantSideDepth),
    sideDepthRate: num(s.sideDepthRate, DEFAULT_FRAMING_SPECS.sideDepthRate),
    sheetRate: num(s.sheetRate, DEFAULT_FRAMING_SPECS.sheetRate),
    stretcherBar: pick(s.stretcherBar, STRETCHER_RATES, DEFAULT_FRAMING_SPECS.stretcherBar),
    frameType: s.frameType === 'Floater Frame' ? 'Floater Frame' : 'Classic Frame',
    frameMaterial: s.frameMaterial === 'Aluminium' ? 'Aluminium' : 'Wooden',
    frameFront: num(s.frameFront, DEFAULT_FRAMING_SPECS.frameFront),
    frameDepth: num(s.frameDepth, DEFAULT_FRAMING_SPECS.frameDepth),
    floaterSpace: num(s.floaterSpace, DEFAULT_FRAMING_SPECS.floaterSpace),
    frameFinish: pick(s.frameFinish, FINISH_ADDON, DEFAULT_FRAMING_SPECS.frameFinish),
  };
}

/**
 * Carry one piece's spec selection over to a piece of a different category.
 *
 * A set is normally one body of work, so this is usually a no-op — but a Paper spec says
 * "Classic Frame" and a Canvas piece can only take a Floater Frame (and vice versa), and pricing
 * a mismatched pair silently returns zero for the frame. Everything the buyer actually chose
 * (widths, depths, glass…) carries over untouched; only the incompatible pair is snapped.
 */
export function specsForArtworkType(
  specs: Partial<FramingSpecs> | null | undefined,
  artworkType: FramingArtworkType,
): FramingSpecs {
  const next = sanitizeSpecs(specs);
  if (artworkType === 'Canvas') {
    next.frameType = 'Floater Frame';
    // Floater + Aluminium is not offered at any size. Falling back to Wooden means a finish
    // add-on now applies, and the buyer never chose one — so take the ₹0 finish rather than
    // inventing an upcharge.
    if (next.frameMaterial === 'Aluminium') {
      next.frameMaterial = 'Wooden';
      next.frameFinish = 'Natural Lacquer';
    }
  } else {
    next.frameType = 'Classic Frame';
  }
  return next;
}

interface Ctx {
  artworkType: FramingArtworkType;
  lengthIn: number;
  heightIn: number;
  specs: FramingSpecs;
}

/** Mount adds its width to every side; every sheet-facing component is billed on that outer size. */
function outerDims(ctx: Ctx) {
  const isMount = ctx.specs.wantMount;
  const Lin = isMount ? ctx.lengthIn + 2 * ctx.specs.mountWidth : ctx.lengthIn;
  const Hin = isMount ? ctx.heightIn + 2 * ctx.specs.mountWidth : ctx.heightIn;
  return { Lin, Hin };
}

function mountResult(ctx: Ctx): FramingComponentResult {
  if (ctx.artworkType === 'Canvas' || !ctx.specs.wantMount) return { applicable: false };
  const { Lin, Hin } = outerDims(ctx);
  const Lft = ladderFeet(Lin), Hft = ladderFeet(Hin);
  if (Lft == null || Hft == null) return { applicable: true, outOfRange: true };
  const area = Lft * Hft;
  const rate = ctx.specs.mountType === 'Single Mount' ? 100 : 200;
  const mn = Math.min(Lin, Hin), mx = Math.max(Lin, Hin);
  let cost;
  if (mn <= 40 && mx <= 60) { cost = Math.max(100, area * rate); }
  else if (mn <= 48 && mx <= 96) { cost = ctx.specs.mountType === 'Single Mount' ? 10000 : 20000; }
  else if (mn <= 60 && mx <= 96) { cost = ctx.specs.mountType === 'Single Mount' ? 16500 : 33000; }
  else { return { applicable: true, outOfRange: true }; }
  return { applicable: true, outOfRange: false, Lin, Hin, Lft, Hft, area, rate, cost };
}

function glassResult(ctx: Ctx): FramingComponentResult {
  if (ctx.artworkType === 'Canvas') return { applicable: false };
  const { Lin, Hin } = outerDims(ctx);
  const Lft = ladderFeet(Lin), Hft = ladderFeet(Hin);
  if (Lft == null || Hft == null) return { applicable: true, outOfRange: true };
  const rate = GLASS_RATES[ctx.specs.glassType]!;
  const mn = Math.min(Lin, Hin), mx = Math.max(Lin, Hin);
  const area = Lft * Hft;
  let cost;
  if (mn <= 48 && mx <= 48) { cost = Math.max(100, area * rate); }
  else if (mn <= 48 && mx <= 71) { cost = 2400 * (rate / 80); }
  else if (mn <= 48 && mx <= 96) { cost = 3200 * (rate / 80); }
  else if (mn > 48 && mx > 48) { cost = 4800 * (rate / 80); }
  else { return { applicable: true, outOfRange: true }; }

  return { applicable: true, outOfRange: false, Lin, Hin, Lft, Hft, area, rate, cost };
}

function boardResult(ctx: Ctx): FramingComponentResult {
  if (ctx.artworkType === 'Canvas') return { applicable: false };
  const { Lin, Hin } = outerDims(ctx);
  const Lft = ladderFeet(Lin), Hft = ladderFeet(Hin);
  if (Lft == null || Hft == null) return { applicable: true, outOfRange: true };
  const area = Lft * Hft;
  const rate = BOARD_RATES[ctx.specs.boardType]!;
  const cost = area * rate;
  return { applicable: true, outOfRange: false, Lin, Hin, Lft, Hft, area, rate, cost };
}

function sideDepthResult(ctx: Ctx): FramingComponentResult {
  if (ctx.artworkType === 'Canvas' || !ctx.specs.wantSideDepth) return { applicable: false };
  const { Lin, Hin } = outerDims(ctx);
  const Lft = ladderFeet(Lin), Hft = ladderFeet(Hin);
  if (Lft == null || Hft == null) return { applicable: true, outOfRange: true };
  const perimeter = 2 * (Lft + Hft);
  const cost = perimeter * ctx.specs.sideDepthRate;
  return { applicable: true, outOfRange: false, Lft, Hft, perimeter, rate: ctx.specs.sideDepthRate, cost };
}

function sheetResult(ctx: Ctx): FramingComponentResult {
  if (ctx.artworkType === 'Canvas') return { applicable: false };
  const { Lin, Hin } = outerDims(ctx);
  const Lft = ladderFeet(Lin), Hft = ladderFeet(Hin);
  if (Lft == null || Hft == null) return { applicable: true, outOfRange: true };
  const area = Lft * Hft;
  const cost = area * ctx.specs.sheetRate;
  return { applicable: true, outOfRange: false, Lft, Hft, area, rate: ctx.specs.sheetRate, cost };
}

function stretcherResult(ctx: Ctx): FramingComponentResult {
  const Lft = ladderFeet(ctx.lengthIn), Hft = ladderFeet(ctx.heightIn);
  if (Lft == null || Hft == null) return { applicable: true, outOfRange: true };
  const totalPerimeter = 3 * (Lft + Hft);
  const rate = STRETCHER_RATES[ctx.specs.stretcherBar]!;
  const cost = totalPerimeter * rate;
  return { applicable: true, outOfRange: false, Lft, Hft, totalPerimeter, rate, cost };
}

function frameResult(ctx: Ctx): FramingComponentResult {
  const { specs } = ctx;
  const notApplicable =
    (ctx.artworkType === 'Paper' && specs.frameType === 'Floater Frame') ||
    (ctx.artworkType === 'Canvas' && specs.frameType === 'Classic Frame') ||
    (specs.frameType === 'Floater Frame' && specs.frameMaterial === 'Aluminium');
  if (notApplicable) return { applicable: false, notApplicable: true };

  let Lin, Hin;
  if (ctx.artworkType === 'Canvas') {
    Lin = ctx.lengthIn + 2 * specs.frameFront;
    Hin = ctx.heightIn + 2 * specs.frameFront;
  } else {
    const isMount = specs.wantMount;
    Lin = isMount ? ctx.lengthIn + 2 * specs.mountWidth + 2 * specs.frameFront : ctx.lengthIn + 2 * specs.frameFront;
    Hin = isMount ? ctx.heightIn + 2 * specs.mountWidth + 2 * specs.frameFront : ctx.heightIn + 2 * specs.frameFront;
  }
  const Lft = ladderFeet(Lin), Hft = ladderFeet(Hin);
  if (Lft == null || Hft == null) return { applicable: true, outOfRange: true };
  const perimeter = 2 * (Lft + Hft);
  const shape = ctx.lengthIn === ctx.heightIn ? 'Square' : 'Rectangular';

  let baseRate;
  if (specs.frameType === 'Floater Frame') {
    baseRate = specs.frameFront * specs.frameDepth * 100;
  } else if (specs.frameMaterial === 'Wooden') {
    baseRate = shape === 'Square' ? 100 : 200;
  } else {
    baseRate = (specs.frameFront <= 1 && specs.frameDepth <= 1) ? 500 : 800;
  }
  const finishAddOn = specs.frameMaterial === 'Wooden' ? FINISH_ADDON[specs.frameFinish]! : 0;
  const finalRate = specs.frameType === 'Floater Frame' ? baseRate + specs.floaterSpace * 100 + finishAddOn : baseRate + finishAddOn;
  const cost = finalRate * perimeter;
  return { applicable: true, outOfRange: false, Lin, Hin, Lft, Hft, perimeter, shape, baseRate, finishAddOn, finalRate, cost };
}

const EMPTY_COMPONENT: FramingComponentResult = { applicable: false };

/** Price one artwork at one spec selection. Pure: same input, same numbers, no Vue involved. */
export function calculateFramingBreakdown(input: {
  artworkType: FramingArtworkType;
  lengthIn: number;
  heightIn: number;
  specs: Partial<FramingSpecs> | null | undefined;
  /** Defaults to the full frame build. */
  mode?: FramingMode;
}): FramingBreakdown {
  const { artworkType, lengthIn, heightIn } = input;
  const mode: FramingMode = input.mode === 'stretch' ? 'stretch' : 'frame';
  const specs = sanitizeSpecs(input.specs);

  // ladderFeet() would happily bill a missing size as its smallest rung, so refuse it outright.
  if (!(lengthIn > 0) || !(heightIn > 0)) {
    return {
      mode,
      components: {
        mount: EMPTY_COMPONENT, glass: EMPTY_COMPONENT, board: EMPTY_COMPONENT,
        sideDepth: EMPTY_COMPONENT, sheet: EMPTY_COMPONENT, stretcher: EMPTY_COMPONENT,
        frame: EMPTY_COMPONENT,
      },
      total: 0,
      outOfRange: false,
      invalidSize: true,
    };
  }

  const ctx: Ctx = { artworkType, lengthIn, heightIn, specs };
  const components: FramingComponents = mode === 'stretch'
    ? {
        // Stretching the canvas and hanging it as-is: the bars are the whole job.
        mount: EMPTY_COMPONENT,
        glass: EMPTY_COMPONENT,
        board: EMPTY_COMPONENT,
        sideDepth: EMPTY_COMPONENT,
        sheet: EMPTY_COMPONENT,
        stretcher: stretcherResult(ctx),
        frame: EMPTY_COMPONENT,
      }
    : {
        mount: mountResult(ctx),
        glass: glassResult(ctx),
        board: boardResult(ctx),
        sideDepth: sideDepthResult(ctx),
        sheet: sheetResult(ctx),
        stretcher: stretcherResult(ctx),
        frame: frameResult(ctx),
      };

  let total = 0;
  let outOfRange = false;
  for (const component of Object.values(components)) {
    if (component.cost) total += component.cost;
    if (component.outOfRange) outOfRange = true;
  }

  return { mode, components, total, outOfRange, invalidSize: false };
}

/**
 * How far a stretched canvas stands off the wall, from the bar's "<face>x<depth>" size.
 * Used by the live preview to draw the returning edge, and by shipping to size the box.
 */
export function stretcherBarDepthInches(stretcherBar?: string | null): number {
  const depth = Number(String(stretcherBar ?? '').split('x')[1]);
  return Number.isFinite(depth) && depth > 0 ? depth : 1.5;
}
